import {
  leadsLogsListOptions,
  opportunitiesLogsListOptions,
  ReturnShortCallReadable,
  ReturnTaskReadable
} from '@crm/api'
import { AudioPlayer } from '@crm/components'
import LogsPanelContext from '@crm/contexts/LogsPanelContext'
import { OpportunityContext } from '@crm/contexts/OpportunityContext'
import { usePaginationConfigCrm } from '@crm/hooks'
import { useQueryParams } from '@shared/hooks'
import { useQuery } from '@tanstack/react-query'
import { Empty, Flex, Pagination, Spin } from 'antd'
import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ActivityFilters } from './ActivityFilters'
import { ActivityLogItem } from './ActivityLogItem'
import { CreateMeeting, CreateNote, SendMail } from './ExtraComponents'
import { CompleteTask } from '../TasksCol/CompleteTask'

export const ActivityCol = () => {
  const { id, opportunityId } = useParams()
  const params = useQueryParams()
  const { type } = useContext(OpportunityContext)

  const [completeTask, setCompleteTask] = useState<ReturnTaskReadable>()
  const [callItem, setCallItem] = useState<ReturnShortCallReadable>()
  const [openSendMail, setOpenSendMail] = useState(false)
  const [openCreateNote, setOpenCreateNote] = useState(false)
  const [openCreateMeeting, setOpenCreateMeeting] = useState(false)

  const { data: leadData, isLoading: isLeadLoading } = useQuery({
    ...leadsLogsListOptions({ path: { lead_id: String(id) }, query: params }),
    enabled: !!id && type === 'lead'
  })

  const { data: opportunityData, isLoading: isOpportunityLoading } = useQuery({
    ...opportunitiesLogsListOptions({
      path: { opportunity_id: String(opportunityId) },
      query: params
    }),
    enabled: !!opportunityId && type === 'opportunity'
  })

  const logs = leadData || opportunityData
  const isLoading = isLeadLoading || isOpportunityLoading

  const pagination = usePaginationConfigCrm(logs)

  const onToggleSendMail = () => setOpenSendMail((prev) => !prev)

  const onToggleCreateNote = () => setOpenCreateNote((prev) => !prev)

  const handleToggleCreateMeeting = () => setOpenCreateMeeting((prev) => !prev)

  const onOpenPlayer = (call: ReturnShortCallReadable) => setCallItem(call)

  const onOpenCompleteTask = (task: ReturnTaskReadable) => setCompleteTask(task)

  return (
    <LogsPanelContext.Provider
      value={{
        isOpenSendMail: openSendMail,
        toggleSendMail: onToggleSendMail,
        isOpenCreateNote: openCreateNote,
        toggleCreateNote: onToggleCreateNote,
        isOpenCreateMeeting: openCreateMeeting,
        toggleCreateMeeting: handleToggleCreateMeeting,
        callItem,
        openPlayer: onOpenPlayer,
        closePlayer: () => setCallItem(undefined),
        openCompleteTask: onOpenCompleteTask
      }}
    >
      <Flex vertical gap={8}>
        <ActivityFilters />

        <Spin spinning={isLoading}>
          <Flex vertical gap={16}>
            {openSendMail && <SendMail />}

            {openCreateNote && <CreateNote />}

            {openCreateMeeting && <CreateMeeting />}

            {logs && <ActivityLogItem logs={logs.results} />}

            {logs?.results.length === 0 && <Empty />}

            {logs && <Pagination {...pagination} />}
          </Flex>
        </Spin>
      </Flex>

      <CompleteTask
        open={!!completeTask}
        task={completeTask as ReturnTaskReadable}
        onCancel={() => setCompleteTask(undefined)}
      />

      <div className="player-wrapper">
        {callItem && (
          <AudioPlayer
            record={callItem}
            onClose={() => setCallItem(undefined)}
          />
        )}
      </div>
    </LogsPanelContext.Provider>
  )
}
